import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { EventInstance, EventInstanceSite, EventSiteRelease } from '../../../types';
import { groupEventService } from '../../../services/groupEventService';
import { authService } from '../../../services/auth';
import EventInstanceForm from './EventInstanceForm';
import EventSiteSelector from './EventSiteSelector';
import EventSiteReleaseManager from './EventSiteReleaseManager';

type EventStatusType = 'active' | 'upcoming' | 'past';

// Parse date string as local date (noon to avoid timezone issues)
const parseLocalDate = (dateString: string): Date => {
  return new Date(dateString + 'T12:00:00');
};

const getEventStatus = (startDate: string, endDate: string): EventStatusType => {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);

  if (today >= start && today <= end) {
    return 'active';
  } else if (today < start) {
    return 'upcoming';
  } else {
    return 'past';
  }
};

const formatDateRange = (startDate: string, endDate: string): string => {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };

  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', options);

  // If same year, only show year on end date
  if (start.getFullYear() === end.getFullYear()) {
    return `${startStr} - ${endStr}`;
  }

  return `${start.toLocaleDateString('en-US', options)} - ${endStr}`;
};

const EventInstanceList: React.FC = () => {
  const [events, setEvents] = useState<EventInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includePast, setIncludePast] = useState(false);

  // Modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedEvent, setSelectedEvent] = useState<EventInstance | null>(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<EventInstance | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Site selector state
  const [isSiteSelectorOpen, setIsSiteSelectorOpen] = useState(false);
  const [siteSelectionEvent, setSiteSelectionEvent] = useState<EventInstance | null>(null);
  const [eventSites, setEventSites] = useState<{ [eventId: number]: EventInstanceSite[] }>({});
  const [loadingSites, setLoadingSites] = useState(false);

  // Release manager state
  const [isReleaseManagerOpen, setIsReleaseManagerOpen] = useState(false);
  const [releaseEvent, setReleaseEvent] = useState<EventInstance | null>(null);
  const [eventReleases, setEventReleases] = useState<{ [eventId: number]: EventSiteRelease[] }>({});
  const [loadingReleases, setLoadingReleases] = useState(false);

  // Get store and corporate codes from auth service (set during login)
  const storeCode = authService.getStoreCode() || '';
  const corporateCode = authService.getCorporateCode() || '';

  const storeId = authService.getStoreId();

  // Load instances from API
  const loadInstances = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await groupEventService.getInstances(storeId || undefined, undefined, true, includePast);
      setEvents(data);
    } catch (err) {
      console.error('Error loading instances:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [storeId, includePast]);

  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  const filteredEvents = useMemo(() => {
    // Already filtered by API based on includePast, just sort
    const sorted = [...events];
    sorted.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    return sorted;
  }, [events]);

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const handleEditEvent = (event: EventInstance) => {
    setSelectedEvent(event);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleSaveEvent = async (eventData: Partial<EventInstance>) => {
    try {
      if (formMode === 'create') {
        await groupEventService.createInstance({
          store_id: storeId || 1,
          template_id: eventData.template_id,
          booking_code: eventData.booking_code || '',
          name: eventData.name || '',
          description: eventData.description,
          start_date: eventData.start_date || '',
          end_date: eventData.end_date || '',
          booking_start_date: eventData.booking_start_date,
          booking_end_date: eventData.booking_end_date,
          sites_blocked: eventData.sites_blocked ?? true,
          min_nights: eventData.min_nights,
          max_nights: eventData.max_nights,
          max_sites_per_booking: eventData.max_sites_per_booking || 1,
          stay_buffer_days: eventData.stay_buffer_days,
          default_price: eventData.default_price,
          active: eventData.active ?? true,
        });
      } else if (selectedEvent) {
        await groupEventService.updateInstance(selectedEvent.id, {
          template_id: eventData.template_id,
          booking_code: eventData.booking_code,
          name: eventData.name,
          description: eventData.description,
          start_date: eventData.start_date,
          end_date: eventData.end_date,
          booking_start_date: eventData.booking_start_date,
          booking_end_date: eventData.booking_end_date,
          sites_blocked: eventData.sites_blocked,
          min_nights: eventData.min_nights,
          max_nights: eventData.max_nights,
          max_sites_per_booking: eventData.max_sites_per_booking,
          stay_buffer_days: eventData.stay_buffer_days,
          default_price: eventData.default_price,
          active: eventData.active,
        });
      }
      await loadInstances();
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error saving event:', err);
      alert('Failed to save event. Please try again.');
    }
  };

  const handleManageSites = async (event: EventInstance) => {
    setSiteSelectionEvent(event);
    setIsSiteSelectorOpen(true);

    // Load existing sites for this instance
    try {
      setLoadingSites(true);
      const sites = await groupEventService.getInstanceSites(event.id);
      setEventSites(prev => ({
        ...prev,
        [event.id]: sites
      }));
    } catch (err) {
      console.error('Error loading instance sites:', err);
    } finally {
      setLoadingSites(false);
    }
  };

  const handleSaveSites = async (siteIds: number[]) => {
    if (!siteSelectionEvent) return;

    try {
      // Get current assigned site IDs
      const currentSites = eventSites[siteSelectionEvent.id] || [];
      const currentSiteIds = new Set(currentSites.map(s => s.site_id));
      const newSiteIds = new Set(siteIds);

      // Find sites to add (in new but not in current)
      const sitesToAdd = siteIds.filter(id => !currentSiteIds.has(id));

      // Find sites to remove (in current but not in new)
      const sitesToRemove = currentSites.filter(s => !newSiteIds.has(s.site_id));

      // Add new sites
      if (sitesToAdd.length > 0) {
        await groupEventService.addInstanceSites(siteSelectionEvent.id, {
          site_ids: sitesToAdd
        });
      }

      // Remove old sites
      for (const site of sitesToRemove) {
        await groupEventService.removeInstanceSite(siteSelectionEvent.id, site.site_id);
      }

      // Reload instances to get updated site counts
      await loadInstances();
    } catch (err) {
      console.error('Error saving instance sites:', err);
      alert('Failed to update sites. Please try again.');
    }
  };

  const handleManageReleases = async (event: EventInstance) => {
    setReleaseEvent(event);
    setIsReleaseManagerOpen(true);

    // Load existing sites and releases for this instance
    try {
      setLoadingReleases(true);

      // Load sites if not already loaded
      if (!eventSites[event.id]) {
        const sites = await groupEventService.getInstanceSites(event.id);
        setEventSites(prev => ({
          ...prev,
          [event.id]: sites
        }));
      }

      // Load releases
      const releases = await groupEventService.getInstanceReleases(event.id);
      setEventReleases(prev => ({
        ...prev,
        [event.id]: releases
      }));
    } catch (err) {
      console.error('Error loading releases:', err);
    } finally {
      setLoadingReleases(false);
    }
  };

  const handleSaveReleases = async (releases: EventSiteRelease[]) => {
    if (!releaseEvent) return;

    // The release manager will call the API directly for each toggle
    // Just update local state and reload
    setEventReleases(prev => ({
      ...prev,
      [releaseEvent.id]: releases
    }));
  };

  const handleDeleteEvent = (event: EventInstance) => {
    setDeleteConfirm(event);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      await groupEventService.deleteInstance(deleteConfirm.id);
      await loadInstances();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Get released count for an event
  const getReleasedCount = (eventId: number): number => {
    const releases = eventReleases[eventId] || [];
    return releases.filter(r => r.released).length;
  };

  // Copy booking link to clipboard
  const handleCopyBookingLink = async (event: EventInstance) => {
    if (!storeCode || !corporateCode) {
      alert('Store/Corporate codes not available. Please log out and log back in.');
      return;
    }

    // Base URL for landing page
    const baseUrl = 'https://res.happycamperspro.com';
    const bookingUrl = `${baseUrl}/?c=${corporateCode}&s=${storeCode}&b=${event.booking_code}`;

    try {
      await navigator.clipboard.writeText(bookingUrl);
      alert('Booking link copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers or non-HTTPS contexts
      prompt('Copy this booking link:', bookingUrl);
    }
  };

  if (loading) {
    return (
      <div className="event-instance-list">
        <div className="events-loading">Loading events...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-instance-list">
        <div className="events-error">
          {error}
          <button onClick={loadInstances} className="btn-retry">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="event-instance-list">
      <div className="events-actions">
        <div className="events-actions-left">
          <label className="events-filter">
            <input
              type="checkbox"
              checked={includePast}
              onChange={(e) => setIncludePast(e.target.checked)}
            />
            Include past events
          </label>
        </div>
        <button className="btn-create-event" onClick={handleCreateEvent}>
          + Create Event
        </button>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="events-empty">
          <div className="events-empty-icon">📅</div>
          <div className="events-empty-text">
            {includePast
              ? 'No events found'
              : 'No upcoming events. Create one to get started!'}
          </div>
        </div>
      ) : (
        <table className="events-table">
          <thead>
            <tr>
              <th>Event Name</th>
              <th>Code</th>
              <th>Dates</th>
              <th>Sites</th>
              <th>Bookings</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.map(event => {
              const status = getEventStatus(event.start_date, event.end_date);
              const releasedCount = getReleasedCount(event.id);
              const siteCount = event.site_count || 0;

              return (
                <tr key={event.id}>
                  <td>
                    <div className="event-name">
                      <span className="event-name-link">{event.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="booking-code">{event.booking_code}</span>
                  </td>
                  <td className="date-range">
                    {formatDateRange(event.start_date, event.end_date)}
                  </td>
                  <td className="count-cell">
                    <span title={releasedCount > 0 ? `${releasedCount} released` : undefined}>
                      {siteCount}
                      {releasedCount > 0 && (
                        <span className="released-indicator"> ({releasedCount})</span>
                      )}
                    </span>
                  </td>
                  <td className="count-cell">
                    {event.booking_count || 0}
                  </td>
                  <td>
                    <span className={`event-status ${status}`}>
                      {status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-action"
                        onClick={() => handleEditEvent(event)}
                        title="Edit event"
                      >
                        Edit
                      </button>
                      <button
                        className="btn-action"
                        onClick={() => handleManageSites(event)}
                        title="Manage sites"
                      >
                        Sites
                      </button>
                      {siteCount > 0 && (
                        <button
                          className="btn-action"
                          onClick={() => handleManageReleases(event)}
                          title="Release sites to public"
                        >
                          Release
                        </button>
                      )}
                      <button
                        className="btn-action"
                        onClick={() => handleCopyBookingLink(event)}
                        title="Copy booking link to share"
                      >
                        Copy Link
                      </button>
                      <button
                        className="btn-action danger"
                        onClick={() => handleDeleteEvent(event)}
                        title="Deactivate event"
                      >
                        Deactivate
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Event Form Modal */}
      <EventInstanceForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSaveEvent}
        event={selectedEvent}
        mode={formMode}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deactivate Event</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to deactivate <strong>{deleteConfirm.name}</strong>?</p>
              <p className="confirm-warning">The event will be marked as inactive and hidden from booking.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deactivating...' : 'Deactivate Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site Selector Modal */}
      {siteSelectionEvent && (
        <EventSiteSelector
          isOpen={isSiteSelectorOpen}
          onClose={() => {
            setIsSiteSelectorOpen(false);
            setSiteSelectionEvent(null);
          }}
          onSave={handleSaveSites}
          event={siteSelectionEvent}
          assignedSites={eventSites[siteSelectionEvent.id] || []}
          loading={loadingSites}
        />
      )}

      {/* Site Release Manager Modal */}
      {releaseEvent && (
        <EventSiteReleaseManager
          isOpen={isReleaseManagerOpen}
          onClose={() => {
            setIsReleaseManagerOpen(false);
            setReleaseEvent(null);
          }}
          onSave={handleSaveReleases}
          event={releaseEvent}
          assignedSites={eventSites[releaseEvent.id] || []}
          siteReleases={eventReleases[releaseEvent.id] || []}
          loading={loadingReleases}
        />
      )}
    </div>
  );
};

export default EventInstanceList;
